import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';

// Simple dot animation component
const DotAnimation = () => {
  const [dots, setDots] = useState('');
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);
  return <span>{dots}</span>;
};

const GREETING = 'Hi! I\'m your MyLogistic assistant. You can ask me about shipments, invoices, charges, or upload a document (BOL, AN, DO, AWB) to create a new shipment.';

const ChatWidget = () => {
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: GREETING }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const isAdmin = user && user.role === 'admin';

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, scrollToBottom]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'xls', 'xlsx'].includes(ext)) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Please upload a PDF or Excel file.' }]);
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'File is too large. Maximum size is 20MB.' }]);
      return;
    }

    setPendingFile(file);
    // Reset file input so the same file can be re-selected
    e.target.value = '';
  };

  const removePendingFile = () => {
    setPendingFile(null);
  };

  const handleSend = async () => {
    const text = input.trim();
    if ((!text && !pendingFile) || loading) return;

    // Build user message display
    const displayText = pendingFile
      ? (text ? `📎 ${pendingFile.name}\n${text}` : `📎 ${pendingFile.name}`)
      : text;

    const userMsg = { role: 'user', content: displayText };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      if (pendingFile) {
        // Upload file + optional message
        const formData = new FormData();
        formData.append('file', pendingFile);
        formData.append('message', text || `I uploaded a file: ${pendingFile.name}. Please parse it and help me create a shipment from it.`);
        // Send conversation history
        const history = messages.slice(1).map(m => ({ role: m.role, content: m.content }));
        formData.append('history', JSON.stringify(history));

        setPendingFile(null);

        const res = await api.post('/chat/upload', formData, {
          timeout: 180000,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const reply = res.data.response || 'Document processed.';
        setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      } else {
        // Text-only message
        const history = [...messages.slice(1), userMsg].map(m => ({
          role: m.role,
          content: m.content
        }));

        const res = await api.post('/chat', { messages: history }, { timeout: 120000 });
        const reply = res.data.response || 'Sorry, I could not generate a response.';
        setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      }
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Something went wrong. Please try again.';
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${errMsg}` }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isAdmin) return null;

  return (
    <>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.xls,.xlsx"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          style={styles.fab}
          title="AI Assistant"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div style={styles.window}>
          {/* Header */}
          <div style={styles.header}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <span style={{ fontWeight: 600, fontSize: 14 }}>AI Assistant</span>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={() => {
                  setMessages([{ role: 'assistant', content: GREETING }]);
                  setPendingFile(null);
                }}
                style={styles.headerBtn}
                title="Clear chat"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
              </button>
              <button
                onClick={() => setOpen(false)}
                style={styles.headerBtn}
                title="Close"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div style={styles.body}>
            {messages.map((msg, i) => (
              <div key={i} style={msg.role === 'user' ? styles.userRow : styles.assistantRow}>
                <div style={msg.role === 'user' ? styles.userBubble : styles.assistantBubble}>
                  {msg.content.split('\n').map((line, j) => (
                    <React.Fragment key={j}>
                      {j > 0 && <br />}
                      {line}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
            {loading && (
              <div style={styles.assistantRow}>
                <div style={{ ...styles.assistantBubble, fontStyle: 'italic', color: '#888' }}>
                  <span style={styles.dots}>Thinking<DotAnimation /></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Pending file indicator */}
          {pendingFile && (
            <div style={styles.fileBar}>
              <span style={styles.fileIcon}>📎</span>
              <span style={styles.fileName}>{pendingFile.name}</span>
              <button onClick={removePendingFile} style={styles.fileRemove} title="Remove file">×</button>
            </div>
          )}

          {/* Input */}
          <div style={styles.inputArea}>
            <button
              onClick={() => fileInputRef.current?.click()}
              style={styles.attachBtn}
              title="Upload document (PDF, Excel)"
              disabled={loading}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
            </button>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={pendingFile ? "Add a message (optional)..." : "Ask about shipments, invoices..."}
              rows={1}
              style={styles.textarea}
              disabled={loading}
            />
            <button
              onClick={handleSend}
              disabled={(!input.trim() && !pendingFile) || loading}
              style={{
                ...styles.sendBtn,
                opacity: ((!input.trim() && !pendingFile) || loading) ? 0.5 : 1,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const styles = {
  fab: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    width: 52,
    height: 52,
    borderRadius: '50%',
    backgroundColor: '#1a73e8',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
    zIndex: 9999,
    transition: 'transform 0.2s',
  },
  window: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    width: 400,
    height: 560,
    borderRadius: 12,
    backgroundColor: '#fff',
    boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 9999,
    overflow: 'hidden',
  },
  header: {
    backgroundColor: '#1a73e8',
    color: '#fff',
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
  },
  headerBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    opacity: 0.85,
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    backgroundColor: '#f8f9fa',
  },
  userRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  assistantRow: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  userBubble: {
    maxWidth: '80%',
    padding: '8px 14px',
    borderRadius: '16px 16px 4px 16px',
    backgroundColor: '#1a73e8',
    color: '#fff',
    fontSize: 13,
    lineHeight: 1.5,
    wordBreak: 'break-word',
  },
  assistantBubble: {
    maxWidth: '85%',
    padding: '8px 14px',
    borderRadius: '16px 16px 16px 4px',
    backgroundColor: '#fff',
    color: '#333',
    fontSize: 13,
    lineHeight: 1.5,
    border: '1px solid #e0e0e0',
    wordBreak: 'break-word',
  },
  fileBar: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 12px',
    backgroundColor: '#e8f0fe',
    borderTop: '1px solid #d2e3fc',
    gap: 6,
    flexShrink: 0,
  },
  fileIcon: {
    fontSize: 14,
  },
  fileName: {
    flex: 1,
    fontSize: 12,
    color: '#333',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileRemove: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 18,
    color: '#666',
    padding: '0 4px',
    lineHeight: 1,
  },
  inputArea: {
    display: 'flex',
    alignItems: 'flex-end',
    padding: '10px 12px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#fff',
    gap: 6,
    flexShrink: 0,
  },
  attachBtn: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    backgroundColor: 'transparent',
    border: '1px solid #ddd',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  textarea: {
    flex: 1,
    resize: 'none',
    border: '1px solid #ddd',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 13,
    fontFamily: 'inherit',
    outline: 'none',
    maxHeight: 80,
    lineHeight: 1.4,
  },
  sendBtn: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: '#1a73e8',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  dots: {
    display: 'inline-flex',
    minWidth: 80,
  },
};

export default ChatWidget;
